import {
  Flex,
  Icon,
  MenuItem,
  MenuItemOption,
  MenuOptionGroup,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { FiChevronDown, FiEye } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { OrganizationRoleType } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useSub } from "../subscription/useSub";
import { viewAsRoleOptions } from "../subscription/viewAsRoleLabels";
import { setViewAsRole, useViewAsRole } from "../subscription/viewAsRoleStore";

const OWN_ROLES = "own";

export const useViewAsRoleLabel = (): string | null => {
  const { t } = useTranslation();
  const viewAsRole = useViewAsRole();
  const option = viewAsRoleOptions.find(
    (candidate) => candidate.role === viewAsRole,
  );
  return option ? t(option.labelKey) : null;
};

/**
 * Lets an owner/admin preview the app as a narrower role without reassigning anyone's roles. The
 * preview only filters the UI (useSub narrows its flags from the previewed role); the backend keeps
 * authorizing by the real roles, which is also why only owners/admins get the choice — for them a
 * preview can only ever narrow.
 */
export const ViewAsRoleOptions = () => {
  const { t } = useTranslation();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { isSupplier } = useSub();
  const viewAsRole = useViewAsRole();
  const activeLabel = useViewAsRoleLabel();

  const [expanded, setExpanded] = useState(false);

  if (isSupplier || !selectedOrg.canEdit) return null;

  return (
    <>
      <MenuItem
        closeOnSelect={false}
        icon={<Icon as={FiEye} />}
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
      >
        <Flex align="center" justify="space-between" gap="3">
          <Text>
            {activeLabel
              ? `${t("Viewing as")} ${activeLabel}`
              : t("View as role")}
          </Text>
          <Icon
            as={FiChevronDown}
            boxSize="4"
            color="smMuted"
            flexShrink={0}
            transform={expanded ? undefined : "rotate(-90deg)"}
            transition="transform 0.15s"
          />
        </Flex>
      </MenuItem>
      {expanded && (
        <MenuOptionGroup
          type="radio"
          value={viewAsRole ?? OWN_ROLES}
          onChange={(value) =>
            setViewAsRole(
              value === OWN_ROLES ? null : (value as OrganizationRoleType),
            )
          }
        >
          <MenuItemOption value={OWN_ROLES}>{t("My own roles")}</MenuItemOption>
          {viewAsRoleOptions.map((option) => (
            <MenuItemOption key={option.role} value={option.role}>
              {t(option.labelKey)}
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
      )}
    </>
  );
};
